import React, { useCallback, useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { ui } from '../ui';
import {
  UploadResult, UploadLogEntry,
  uploadScheduleFile, uploadPayrollFile, getUploadLog,
} from '../api';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB, mirrors the server-side cap (FR-10)

const getExtension = (fileName: string) => {
  const idx = fileName.lastIndexOf('.');
  return idx === -1 ? '' : fileName.slice(idx).toLowerCase();
};

// Client-side pre-checks only — the server re-validates both rules independently
// (FR-10); this just gives the admin an immediate answer without a round trip.
const validateFile = (file: File, allowedExtensions: string[]): string | null => {
  const ext = getExtension(file.name);
  if (!allowedExtensions.includes(ext)) {
    return `Unsupported file type — expected ${allowedExtensions.join(' or ')}.`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'File is too large — the maximum upload size is 5 MB.';
  }
  return null;
};

// Surfaces the server's 400 { error } message verbatim (FR-5); falls back to a
// generic retry prompt for network failures / 500s where there's no such body.
const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return 'Upload failed — try again.';
};

// Range bounds come back as YYYY-MM-DD calendar dates; format in UTC-midnight space
// so the viewer's local timezone can't shift the displayed day (mirrors
// TimeTrackingAdmin's fmtGridDate for the coverage grid).
const fmtRangeDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' });

const fmtUploadedAt = (iso: string) => new Date(iso).toLocaleString();

const STATUS_TONE: Record<UploadLogEntry['status'], string> = {
  Success: 'text-green-600',
  PartialSuccess: 'text-amber-600',
  Failed: 'text-red-600',
};
const STATUS_LABEL: Record<UploadLogEntry['status'], string> = {
  Success: 'Success', PartialSuccess: 'Partial', Failed: 'Failed',
};
const FILE_TYPE_LABEL: Record<string, string> = { schedule: 'Schedule', payroll: 'Payroll' };

interface UploadControlProps {
  id: string;
  label: string;
  accept: string;
  allowedExtensions: string[];
  uploadFn: (file: File) => Promise<UploadResult>;
  onUploaded: () => void;
}

// One file input + Upload button, self-contained state machine: idle -> uploading
// -> success summary | error. A fresh file selection clears any prior result/error.
const UploadControl: React.FC<UploadControlProps> = ({ id, label, accept, allowedExtensions, uploadFn, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preCheckError, setPreCheckError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0] ?? null;
    setResult(null);
    setError(null);
    setFile(chosen);
    setPreCheckError(chosen ? validateFile(chosen, allowedExtensions) : null);
  };

  const handleUpload = async () => {
    if (!file || preCheckError || uploading) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const res = await uploadFn(file);
      setResult(res);
      setFile(null);
      onUploaded();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-md border border-border-default bg-surface p-4">
      <label className={ui.label} htmlFor={id}>{label}</label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={id}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={uploading}
          className="block text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 file:mr-3 file:rounded-md file:border file:border-border-default file:bg-surface-alt file:px-3 file:py-1.5 file:text-sm file:text-secondary hover:file:bg-surface-hover"
        />
        <button type="button" className={ui.btnPrimary} onClick={handleUpload} disabled={!file || !!preCheckError || uploading}>
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      <div aria-live="polite" role="status" className="mt-3 text-sm">
        {uploading && <LoadingSpinner size="sm" message="Uploading…" />}
        {!uploading && preCheckError && <p className="text-red-600">{preCheckError}</p>}
        {!uploading && !preCheckError && error && <p className="text-red-600">{error}</p>}
        {!uploading && result && (
          <div className="rounded-md border border-green-300 bg-green-500/10 p-3 dark:border-green-800">
            <p className="font-medium text-green-700 dark:text-green-400">
              {result.fileName} loaded — {fmtRangeDate(result.rangeStart)} – {fmtRangeDate(result.rangeEnd)}
            </p>
            <p className="text-secondary">
              {result.rowsInserted} row{result.rowsInserted === 1 ? '' : 's'} inserted.
              {result.replacedExisting ? ' This replaced previously loaded data for this date range.' : ''}
            </p>
            {result.rowsSkipped > 0 && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-500/10 p-2 text-amber-700 dark:border-amber-800 dark:text-amber-400">
                <p className="font-medium">
                  {result.rowsSkipped} row{result.rowsSkipped === 1 ? '' : 's'} skipped — unrecognized name{result.skippedNames.length === 1 ? '' : 's'}:
                </p>
                <p>{result.skippedNames.join(', ')}</p>
                <p className="mt-1 text-xs">
                  These names need a roster alias before they can be loaded — contact engineering to add one.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface TimeTrackingUploadsProps {
  // Called after every successful upload so the parent can refresh the Weekly
  // Hours table and Coverage grid without a full page reload (FR-4).
  onUploaded: () => void;
}

export const TimeTrackingUploads: React.FC<TimeTrackingUploadsProps> = ({ onUploaded }) => {
  const [log, setLog] = useState<UploadLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(true);

  const loadLog = useCallback(() => {
    setLogLoading(true);
    return getUploadLog(20).then(setLog).finally(() => setLogLoading(false));
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const handleUploaded = useCallback(() => {
    loadLog();
    onUploaded();
  }, [loadLog, onUploaded]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Upload a MakeShift schedule or ADP payroll report directly — replacing the external CS project
        loader for these two file types. Re-uploading a file for dates already loaded replaces that date range's data.
      </p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <UploadControl
          id="upload-schedule"
          label="MakeShift Schedule (.xlsx)"
          accept=".xlsx"
          allowedExtensions={['.xlsx']}
          uploadFn={uploadScheduleFile}
          onUploaded={handleUploaded}
        />
        <UploadControl
          id="upload-payroll"
          label="ADP Payroll Report (.xls)"
          accept=".xls,.xlsx"
          allowedExtensions={['.xls', '.xlsx']}
          uploadFn={uploadPayrollFile}
          onUploaded={handleUploaded}
        />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-secondary">Upload History</h3>
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>When</th>
                <th className={ui.th}>Who</th>
                <th className={ui.th}>Type</th>
                <th className={ui.th}>File</th>
                <th className={ui.th}>Dates</th>
                <th className={`${ui.th} text-right`}>Inserted</th>
                <th className={`${ui.th} text-right`}>Skipped</th>
                <th className={ui.th}>Status</th>
              </tr>
            </thead>
            <tbody className={ui.tbody}>
              {log.map((entry, i) => (
                <tr key={`${entry.uploadedAtUtc}-${entry.fileName}-${i}`} className="hover:bg-surface-hover">
                  <td className={`${ui.td} whitespace-nowrap`}>{fmtUploadedAt(entry.uploadedAtUtc)}</td>
                  <td className={ui.td}>{entry.uploadedBy}</td>
                  <td className={ui.td}>{FILE_TYPE_LABEL[entry.fileType] ?? entry.fileType}</td>
                  <td className={ui.td}>{entry.fileName}</td>
                  <td className={`${ui.td} whitespace-nowrap`}>{fmtRangeDate(entry.rangeStart)} – {fmtRangeDate(entry.rangeEnd)}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>{entry.rowsInserted}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>
                    {entry.rowsSkipped}
                    {entry.skippedNames.length > 0 && (
                      <div className="mt-1 whitespace-normal text-left text-xs text-amber-700 dark:text-amber-400">
                        {entry.skippedNames.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className={`${ui.td} font-medium ${STATUS_TONE[entry.status]}`}>{STATUS_LABEL[entry.status] ?? entry.status}</td>
                </tr>
              ))}
              {!logLoading && log.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted">
                    No uploads yet.
                  </td>
                </tr>
              )}
              {logLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted">
                    Loading upload history…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
